import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { PlyrComponent } from 'ngx-plyr';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AudioPlayerService {
  private audioUrlSubject = new BehaviorSubject<string>('');
  private audioIdSubject = new BehaviorSubject<number | null>(null);
  private itemIdSubject = new BehaviorSubject<number | null>(null);
  private audioTitleSubject = new BehaviorSubject<string>('');
  private showBottomAudioSubject = new BehaviorSubject<boolean>(false);
  private progressPercentageSubject = new BehaviorSubject<number>(0);
  private durationSubject = new BehaviorSubject<string>('00:00');
  private audioElement: HTMLAudioElement;
  isAudioPausedSubject = new BehaviorSubject<boolean>(true);
  isPausedMapSubject = new BehaviorSubject<Map<number | null, boolean | number>>(new Map<number | null, boolean | number>());

  mainPlayer: PlyrComponent | null = null;
  bottomPlayer: PlyrComponent | null = null;

  private readonly baseUrl = environment.path;

  constructor() { 
    this.audioElement = new Audio();
    this.audioElement.preload = 'auto';
    this.audioElement.addEventListener('ended', this.onAudioEnd.bind(this));
  }

  private onAudioEnd(): void {
    this.audioUrlSubject.next('');
    this.showBottomAudioSubject.next(false);
  }

  playBottomPlayer(): void {
    if (this.bottomPlayer && this.audioElement) {
      this.showBottomAudioSubject.next(true);
      this.bottomPlayer.player.play();
    }
  }

  togglePlayPause(): void {
    if (this.bottomPlayer) {
      this.bottomPlayer.player.pause();
    }
  }

  stopAudio(): void {
    this.bottomPlayer?.player.stop();
    this.showBottomAudioSubject.next(false);
    this.progressPercentageSubject.next(0);
    this.audioUrlSubject.next('');
    this.audioIdSubject.next(null);
    const currentMap = this.isPausedMapSubject.getValue();
    currentMap.forEach((value, key) => {
      currentMap.set(key, false);
    });
  }

  seekTo(time: number): void {
    if (this.bottomPlayer) {
      this.bottomPlayer.player.currentTime = time;
    }
  }

  setPlayers(mainPlayer: PlyrComponent | null, bottomPlayer: PlyrComponent): void {
    this.mainPlayer = mainPlayer;
    this.bottomPlayer = bottomPlayer;

    this.bottomPlayer?.player.on('timeupdate', this.updateProgressPercentage.bind(this));
  }

  setAudio(audio: any): void {
    const absoluteUrl = this.resolveAudioUrl(audio.url);
    this.audioUrlSubject.next(absoluteUrl);
    this.audioIdSubject.next(audio.id);
    this.progressPercentageSubject.next(0);
    this.updateBottomPlayerSource(absoluteUrl);
  }

  compareAudiosIds(id: number): boolean {    
    const currentAudioId = this.audioIdSubject.getValue();
    return currentAudioId === id;
  }
  

  private resolveAudioUrl(url: string): string {
    return new URL(url, this.baseUrl).href;
  }

  private updateBottomPlayerSource(url: string): void {
    if (this.bottomPlayer) {
      this.bottomPlayer.player.source = {
        type: 'audio',
        sources: [{ src: url, type: 'audio/mp3' }]
      };
    }
  }

  private updateProgressPercentage(): void {
    if (this.bottomPlayer) {
      const currentTime = this.bottomPlayer.player.currentTime;
      const duration = this.bottomPlayer.player.duration;
      if (currentTime && duration) {
        const progressPercentage = (currentTime / duration) * 100;
        const remainingTime = duration - currentTime;
        const formattedRemainingTime = this.convertSecondsToTimeFormat(remainingTime);      
        this.progressPercentageSubject.next(progressPercentage);
        this.durationSubject.next(formattedRemainingTime);
      }
    }
  }

  convertSecondsToTimeFormat(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
  
    const formattedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const formattedSeconds = remainingSeconds < 10 ? `0${remainingSeconds}` : `${remainingSeconds}`;
  
    return `${formattedMinutes}:${formattedSeconds}`;
  }

  compareAudios(url: string): boolean {
    const absoluteUrl = this.resolveAudioUrl(url);
    return this.audioElement.src === absoluteUrl;
  }

  getAudioUrl() {
    return this.audioUrlSubject.asObservable();
  }

  getAudioTitle() {
    return this.audioTitleSubject.asObservable();
  }

  setAudioTitle(title: string) {
    this.audioTitleSubject.next(title);
  }

  getShowBottomAudio() {
    return this.showBottomAudioSubject.asObservable();
  }

  setProgressPercentage(percentage: number) {
    this.progressPercentageSubject.next(percentage);
  }

  getProgressPercentage() {
    return this.progressPercentageSubject.asObservable();
  }

  getDuration() {
    return this.durationSubject.asObservable();
  }

  getIsPausedStatus() {
    return this.isAudioPausedSubject.asObservable()
  }

  setDuration(duration: string) {
    this.durationSubject.next(duration);
  }

  setItemId(itemId: number) {
    this.itemIdSubject.next(itemId);
  }

  getItemId() {
    return this.itemIdSubject.asObservable();
  }

  getAudioId() {
    return this.audioIdSubject.value;
  }

  getAudioIdAsObservable() {
    return this.audioIdSubject.asObservable();
  }

  get isPausedMap$() {
    return this.isPausedMapSubject.asObservable();
  }

  updateIsPausedMap(key: number | null, value: boolean | number) {
    const currentMap = this.isPausedMapSubject.getValue();
    currentMap.set(key, value);
    this.isPausedMapSubject.next(new Map(currentMap));
  }
}
